import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import { fmtDate } from "@/lib/dt";   // Batch 10 · B9 (F-82): DD-MMM-YYYY, never the stored ISO
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function DrilldownModal({ engagementId, track, period,
                                          category, onClose }) {
  const colW = useResizableColumns({ storageKey: "da-drilldown-modal", defaults: [130, 110, 240, 130, 130, 160], mins: [96, 72, 130, 90, 90, 100] });   // Step 11d · add-on
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadPage = useCallback(async (after) => {
    setLoading(true); setErr("");
    try {
      const params = { track, period, category };
      if (after) params.after = after;
      const r = await api.get(
        `/digital-audit/${engagementId}/drilldown`, { params });
      const next = r.data?.rows || [];
      setRows((prev) => after ? [...prev, ...next] : next);
      setCursor(r.data?.next_cursor || null);
      setHasMore(!!r.data?.has_more);
    } catch (e) {
      setErr(toErrMsg(e, "Failed to load drill-down"));
    } finally { setLoading(false); }
  }, [engagementId, track, period, category]);

  useEffect(() => { loadPage(null); }, [loadPage]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl"
                      data-testid="drilldown-modal">
        <DialogHeader>
          <DialogTitle>
            {period} · {category} — underlying GL entries
          </DialogTitle>
        </DialogHeader>
        {err && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
               data-testid="drilldown-error">{err}</div>
        )}
        <div className="max-h-[60vh] overflow-y-auto">
          <ResizableTable cols={colW} className="w-full text-sm">
            <thead className="sticky top-0 bg-white text-start text-xs uppercase text-muted-foreground">
              <tr>
                <ResizableTh index={0} className="py-2 pe-3">{t("common.date")}</ResizableTh>
                <ResizableTh index={1} className="py-2 pe-3">{t("common.account")}</ResizableTh>
                <ResizableTh index={2} className="py-2 pe-3">{t("common.description")}</ResizableTh>
                <ResizableTh index={3} className="py-2 pe-3 text-end">{t("common.debit")}</ResizableTh>
                <ResizableTh index={4} className="py-2 pe-3 text-end">{t("common.credit")}</ResizableTh>
                <ResizableTh index={5} className="py-2 pe-3">{t("digitalAudit.drilldown.postedBy")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t"
                    data-testid="drilldown-row">
                  <td className="py-1.5 pe-3">{fmtDate(r.entry_date)}</td>
                  <td className="py-1.5 pe-3">
                    <span className="font-mono text-xs">{r.account_code}</span>
                    {" "}{r.account_name}
                  </td>
                  <td className="py-1.5 pe-3">{r.description}</td>
                  <td className="py-1.5 pe-3 text-end font-mono">
                    {(r.debit || 0).toFixed(2)}
                  </td>
                  <td className="py-1.5 pe-3 text-end font-mono">
                    {(r.credit || 0).toFixed(2)}
                  </td>
                  <td className="py-1.5 pe-3">{r.posted_by || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    {t("digitalAudit.drilldown.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </ResizableTable>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground"
               data-testid="drilldown-count">
            Loaded {rows.length} rows{hasMore ? " (more available)" : ""}
          </div>
          <div className="flex gap-2">
            {hasMore && (
              <Button size="sm" onClick={() => loadPage(cursor)}
                       disabled={loading}
                       data-testid="drilldown-load-more">
                {loading && <Loader2 className="h-3 w-3 me-1 animate-spin" />}
                Load more
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onClose}
                     data-testid="drilldown-close">{t("common.close")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
